import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ShieldCheck, CheckCircle2, Loader2, FileDown } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AgreementReader, { SignProgress, useAgreement } from "@/components/AgreementReader";
import PaymentOptions from "@/components/PaymentOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLAUSES = [
  { id: "ruo", testid: "waiver-checkbox-ruo", text: "I certify these compounds will be utilized solely for in-vitro laboratory research, reagent testing, or educational investigation." },
  { id: "no-human", testid: "waiver-checkbox-no-human", text: "I acknowledge that products are NOT for human, veterinary, or medical diagnostic administration." },
  { id: "storage", testid: "waiver-checkbox-storage", text: "I confirm that our facility maintains proper bio-safety protocols, PPE, and storage capabilities (-20°C lyophilized)." },
  { id: "hold", testid: "waiver-checkbox-holdharmless", text: "I agree to hold Arias Peptides harmless from any misuse, improper handling, or non-compliant distribution." },
];

const EMPTY = { name: "", entity: "", email: "", phone: "", address: "", research_field: "" };

export default function CheckoutModal() {
  const { items, clear, checkoutOpen, setCheckoutOpen, subtotal } = useCart();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [checks, setChecks] = useState([false, false, false, false]);
  const [signature, setSignature] = useState("");
  const [title, setTitle] = useState("");
  const [readComplete, setReadComplete] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const agreement = useAgreement();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (checkoutOpen && user) {
      setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
    }
  }, [checkoutOpen, user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const detailsValid = form.name.trim().length >= 2 && /\S+@\S+\.\S+/.test(form.email);
  const waiverValid = !!agreement && readComplete && acknowledged && checks.every(Boolean) && signature.trim().length >= 3;
  const shipping = subtotal >= 300 ? 0 : 15;

  const reset = () => { setStep(1); setForm(EMPTY); setChecks([false, false, false, false]); setSignature(""); setTitle(""); setReadComplete(false); setAcknowledged(false); setResult(null); };

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        customer: form,
        items: items.map((i) => ({ product_id: i.product.id, qty: i.qty })),
        waiver: { signature_name: signature, signer_title: title, checks, agreement_version: agreement.version, acknowledged, read_complete: readComplete },
      });
      setResult(data);
      setStep(3);
      clear();
      toast.success("Order request submitted and waiver recorded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please review and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={checkoutOpen} onOpenChange={(o) => { setCheckoutOpen(o); if (!o) reset(); }}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[92vh] overflow-y-auto" data-testid="waiver-modal-container">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {step === 1 && "Order Request — Contact & Institution"}
            {step === 2 && "One quick signature before your order is filed"}
            {step === 3 && "Order Request Received"}
          </DialogTitle>
          <DialogDescription className="font-tech text-[11px] uppercase tracking-[0.14em] text-amber-700">
            Step {step} of 3 · {step === 3 ? "Choose a payment method" : "No payment collected at this stage"}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label htmlFor="co-name">Full Legal Name *</Label>
                <Input id="co-name" value={form.name} onChange={set("name")} data-testid="checkout-name-input" className="rounded-sm mt-1.5" /></div>
              <div><Label htmlFor="co-entity">Lab / Entity Name</Label>
                <Input id="co-entity" value={form.entity} onChange={set("entity")} data-testid="checkout-entity-input" className="rounded-sm mt-1.5" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label htmlFor="co-email">Email *</Label>
                <Input id="co-email" type="email" value={form.email} onChange={set("email")} data-testid="checkout-email-input" className="rounded-sm mt-1.5" /></div>
              <div><Label htmlFor="co-phone">Phone</Label>
                <Input id="co-phone" value={form.phone} onChange={set("phone")} data-testid="checkout-phone-input" className="rounded-sm mt-1.5" /></div>
            </div>
            <div><Label htmlFor="co-address">Shipping Address</Label>
              <Input id="co-address" value={form.address} onChange={set("address")} data-testid="checkout-address-input" className="rounded-sm mt-1.5" /></div>
            <div><Label htmlFor="co-field">Intended Research Field</Label>
              <Input id="co-field" placeholder="e.g. in-vitro receptor binding assay" value={form.research_field} onChange={set("research_field")} data-testid="checkout-field-input" className="rounded-sm mt-1.5" /></div>
            <div className="flex justify-between items-center border-t border-slate-200 pt-4">
              <p className="font-tech text-sm text-slate-700">{items.length} line item(s) · ${(subtotal + shipping).toFixed(2)}</p>
              <Button disabled={!detailsValid || items.length === 0} onClick={() => setStep(2)}
                data-testid="checkout-continue-btn">
                Continue to Agreement
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              One signature before your order request is filed. Please read the Agreement all the way through,
              acknowledge it, and sign the current version — it takes about two minutes.
            </p>
            <AgreementReader agreement={agreement} onReadComplete={setReadComplete} className="h-60 sm:h-72" />
            <div className="border border-amber-200 bg-amber-50 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-amber-700" />
                <p className="font-tech text-[11px] uppercase tracking-[0.16em] text-amber-800 font-semibold">RUO Attestations</p>
              </div>
              {CLAUSES.map((c, i) => (
                <div key={c.id} className="flex items-start gap-3">
                  <Checkbox id={c.id} checked={checks[i]} data-testid={c.testid}
                    onCheckedChange={(v) => setChecks(checks.map((x, j) => (j === i ? !!v : x)))} className="mt-0.5" />
                  <Label htmlFor={c.id} className="text-sm text-slate-700 leading-snug cursor-pointer">{c.text}</Label>
                </div>
              ))}
            </div>
            <div className="flex items-start gap-3 border border-slate-200 p-4">
              <Checkbox id="agreement-ack" checked={acknowledged} disabled={!readComplete} data-testid="agreement-acknowledge-checkbox"
                onCheckedChange={(v) => setAcknowledged(!!v)} className="mt-0.5" />
              <Label htmlFor="agreement-ack" className={`text-sm leading-snug cursor-pointer ${readComplete ? "text-slate-900" : "text-slate-400"}`}>
                I have read, understood, and agree to the {agreement?.title || "Arias Peptides Research Purchaser Agreement"}
                {agreement ? ` (${agreement.version})` : ""}.{!readComplete && " Scroll to the end of the Agreement to enable."}
              </Label>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label htmlFor="waiver-sig">Full Legal Name (Signature) *</Label>
                <Input id="waiver-sig" placeholder="Full legal name" value={signature} onChange={(e) => setSignature(e.target.value)}
                  data-testid="waiver-signature-input" className="rounded-sm mt-1.5 font-display italic" /></div>
              <div><Label htmlFor="waiver-title">Professional Title</Label>
                <Input id="waiver-title" placeholder="e.g. Principal Investigator" value={title} onChange={(e) => setTitle(e.target.value)}
                  data-testid="waiver-title-input" className="rounded-sm mt-1.5" /></div>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 border-t border-slate-200 pt-4">
              <SignProgress read={readComplete} acknowledged={acknowledged} named={signature.trim().length >= 3} />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="waiver-back-btn">Back</Button>
                <Button disabled={!waiverValid || sending} onClick={submit} data-testid="waiver-submit-order-btn" className="gap-2">
                  {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {sending ? "Submitting..." : "Sign and Continue"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div className="py-2 space-y-4">
            <div className="text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
              <p className="text-sm text-slate-600 mt-3">Your order request and signed Research Purchaser Agreement are on file.</p>
            </div>
            <div className="border border-slate-200 bg-slate-50 p-5 text-center">
              <p className="font-tech text-[11px] uppercase tracking-[0.18em] text-slate-500">Order Reference</p>
              <p className="font-tech text-2xl font-semibold text-slate-900 mt-1" data-testid="order-confirmation-id">{result.order_id}</p>
              <p className="font-tech text-sm text-slate-600 mt-2">Total: ${result.total.toFixed(2)}</p>
            </div>
            <PaymentOptions orderId={result.order_id} email={form.email} payment={{ status: "unpaid" }} total={result.total} />
            <a href={`${API}/orders/agreement-pdf?order_id=${result.order_id}&email=${encodeURIComponent(form.email)}`} target="_blank" rel="noopener noreferrer"
              data-testid="order-confirmation-agreement-pdf" className="flex items-center justify-center gap-2 border border-slate-200 bg-white hover:border-slate-900 text-sm font-medium text-slate-800 py-2.5 rounded-full transition-colors">
              <FileDown className="h-4 w-4" /> Download your signed Agreement (PDF)
            </a>
            <p className="text-xs text-slate-500 leading-relaxed text-center">
              {result.email_sent
                ? "A copy of your signed Agreement, order summary and payment link has been emailed to you."
                : "Email delivery is currently unavailable — save your Order ID for tracking."}{" "}
              Keep your Order ID and email to track your request.
            </p>
            <Link to="/track" onClick={() => setCheckoutOpen(false)}>
              <Button variant="outline" data-testid="order-confirmation-track-btn" className="w-full">Track This Order</Button>
            </Link>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
